import md5 from './md5';
import Manager from './Manager';
import RestService from 'restService';
import { getGravatarUrl, getLocale } from './Utils';
import * as Sentry from '@sentry/vue';

declare const Keycloak;
declare const window;
declare const KEYCLOAK_CLIENT_ID;
declare const SSO_SERVICE_URL;
declare const ONBOARD_DOMAIN;

export default class User {
  private uid: string;

  public firstName: string;

  public lastName: string;

  private email: string;

  public organization: string;

  public role: string;

  public purpose: string;

  public otherPurpose: string;

  public acceptedPrivacyPolicy: boolean;

  private avatar: string = '';

  private admin: boolean = false;

  public isOnboarded: boolean = false;

  public initialAssessmentCreated: boolean = false;

  public initialProgramName: string;

  init(): Promise<void> {
    return new Promise((resolve, reject) => {
      window.kc = new Keycloak({ realm: 'mentor-canada', clientId: KEYCLOAK_CLIENT_ID });
      const keyCloakOptions = {
        messageReceiveTimeout: 100000,
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      };
      window.kc.init(keyCloakOptions)
        .success(async () => {
          if (window.kc.authenticated) {
            this.uid = window.kc.tokenParsed.sub;
            this.firstName = window.kc.tokenParsed.given_name;
            this.lastName = window.kc.tokenParsed.family_name;
            this.email = window.kc.tokenParsed.email;
            Sentry.setUser({ email: this.email });
            if (window.kc.tokenParsed.realm_access.roles.indexOf('admin') !== -1) {
              this.admin = true;
            }
            this.avatar = await getGravatarUrl(this.email);
            const initResponse = await RestService.post('/init');
            this.isOnboarded = !!initResponse.data.isOnboarded;
            this.initialAssessmentCreated = !!initResponse.data.initialAssessmentCreated;
            this.initialProgramName = initResponse.data.program;
            this.organization = initResponse.data.organization;
            this.role = initResponse.data.role;
            this.purpose = initResponse.data.purpose;
            this.otherPurpose = initResponse.data.other_purpose;
            this.acceptedPrivacyPolicy = initResponse.data.acceptedPrivacyPolicy;
          }
          resolve();
        })
        .error(() => {
          reject();
        });
    });
  }

  getUid(): string {
    return this.uid;
  }

  getName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  getInitials(): string {
    const firstInitial = this.firstName.charAt(0);
    const lastInitial = this.lastName.charAt(0);
    return `${firstInitial}${lastInitial}`;
  }

  getEmail(): string {
    return this.email;
  }

  getAvatar(): string {
    return this.avatar;
  }

  signIn(): void {
    this.redirectToSignIn();
  }

  onboard(): void {
    const lang = getLocale();
    const dest = window.location.href;
    const url = `https://${ONBOARD_DOMAIN}/${lang}/start?dest=${dest}`;
    window.location.href = url;
  }

  signOut(): void {
    localStorage.clear();
    const lang = Manager.getInstance().i18n.global.locale;
    window.kc.updateToken(30)
      .success(() => {
        window.kc.logout({
          redirectUri: `https://${window.location.hostname}/${lang}`,
        });
      })
      .error(() => {
        window.location.reload();
      });
  }

  redirectToSignIn(): void {
    const lang = Manager.getInstance().i18n.global.locale;
    const dest = window.location.href;
    const url = `https://${SSO_SERVICE_URL}/${lang}/user/sign-in?dest=${dest}`;
    window.location.href = url;
  }

  isAdmin(): boolean {
    return this.admin;
  }
}
